import React from 'react'
import './GoogleConnectionStyle.css'
import { Badge, Button, SectionCard } from '@enamelai/design-system'
import useGoogleWorkspace from './useGoogleWorkspace'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'

type GoogleConnectionProps = {
  onMailboxConnected?: () => Promise<void>
  onMailboxDisconnected?: () => Promise<void>
}

const GoogleConnection = ({
  onMailboxConnected,
  onMailboxDisconnected,
}: GoogleConnectionProps): React.ReactElement | null => {
  const {
    status,
    loading,
    connecting,
    syncing,
    handleConnect,
    handleDisconnect,
    handleSync,
  } = useGoogleWorkspace({ onMailboxConnected, onMailboxDisconnected })

  const isConnected = Boolean(status?.isConnected)

  return (
    <SectionCard
      title="Google mailbox"
      headerAfterTitle={
        <span
          className="ds-section-card__icon-button"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Connect a Gmail / Google Workspace account to send and sync email."
        >
          <InfoIcon aria-hidden="true" />
        </span>
      }
      className="scp-email-google-section-card"
    >
      {isConnected ? (
        <span className="scp-email-google-status--connected">
          Connected as {status?.googleEmail}
        </span>
      ) : (
        <Badge mode="tinted" color="red" label="Not connected" />
      )}
      <div className="scp-email-google-actions">
        {isConnected ? (
          <>
            <Button
              size="sm"
              type="button"
              variant="primary"
              disabled={loading || syncing}
              onClick={() => {
                void handleSync()
              }}
            >
              {syncing ? 'Syncing…' : 'Sync emails'}
            </Button>
            <Button
              size="sm"
              type="button"
              variant="secondary"
              destructive
              disabled={loading}
              onClick={() => {
                void handleDisconnect()
              }}
            >
              Disconnect
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            type="button"
            variant="primary"
            disabled={loading || connecting}
            onClick={() => {
              void handleConnect()
            }}
          >
            {connecting ? 'Connecting…' : 'Connect Google'}
          </Button>
        )}
      </div>
    </SectionCard>
  )
}

export default GoogleConnection
